import React from "react";
import { User, Linkedin, ExternalLink } from "lucide-react";
import { getLinkedInAvatarUrl } from "@/api/linkedinService";

/**
 * ============================================================
 * WORKSHOP INSTRUCTOR CARD
 * ============================================================
 * Displays instructor avatar, credentials, biography, and LinkedIn link.
 * Supports single instructor or multiple instructors seamlessly.
 */
export default function WorkshopInstructorCard({ instructor, instructors }) {
  const list =
    Array.isArray(instructors) && instructors.length > 0
      ? instructors
      : instructor && instructor.name
      ? [instructor]
      : [];

  if (list.length === 0) {
    return null;
  }

  const getInitials = (name) => {
    if (!name) return "IT";
    return (
      name
        .split(" ")
        .map((part) => part[0])
        .filter(Boolean)
        .slice(0, 2)
        .join("")
        .toUpperCase() || "IT"
    );
  };

  const isMultiple = list.length > 1;

  return (
    <section aria-labelledby="instructor-heading" className="w-full">
      <div className="rounded-3xl border border-white/10 bg-white/[0.04] p-6 backdrop-blur-2xl shadow-[0_20px_60px_rgba(0,0,0,0.45)] space-y-5">
        {/* Section Title */}
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-xl bg-primary/15 border border-primary/25 text-primary flex items-center justify-center flex-shrink-0">
            <User className="w-4 h-4" />
          </div>
          <div>
            <h2 id="instructor-heading" className="text-lg font-bold tracking-tight text-white">
              {isMultiple ? "Meet Your Instructors" : "Meet Your Instructor"}
            </h2>
            <p className="text-[11px] sm:text-xs text-white/60">
              Practitioner-led instruction from engineering leaders.
            </p>
          </div>
        </div>

        {/* Instructor Profiles */}
        <div className="space-y-4">
          {list.map((person, index) => {
            const initials = getInitials(person.name);
            const linkedinHref = person.linkedin_url
              ? person.linkedin_url.startsWith("http")
                ? person.linkedin_url
                : `https://${person.linkedin_url}`
              : null;

            return (
              <div
                key={person.id || person.name || index}
                className="space-y-4 p-4 sm:p-5 rounded-2xl border border-white/10 bg-white/[0.02] hover:border-white/20 transition-colors"
              >
                {/* Header: Avatar + Details */}
                <div className="flex items-center gap-3.5">
                  {/* Avatar Container */}
                  <div className="relative w-14 h-14 rounded-2xl overflow-hidden border border-white/15 bg-white/5 flex-shrink-0 shadow-lg">
                    {person.avatar_url || (person.linkedin_url ? getLinkedInAvatarUrl(person.linkedin_url) : null) ? (
                      <img
                        src={person.avatar_url || getLinkedInAvatarUrl(person.linkedin_url)}
                        alt={person.name}
                        className="w-full h-full object-cover object-center"
                        onError={(e) => {
                          e.target.style.display = "none";
                          const fallback = e.target.nextSibling;
                          if (fallback) fallback.style.display = "flex";
                        }}
                      />
                    ) : null}
                    <div
                      className={`w-full h-full flex items-center justify-center bg-gradient-to-br from-primary/25 via-primary/10 to-transparent text-primary font-bold text-lg ${
                        person.avatar_url || person.linkedin_url ? "hidden" : "flex"
                      }`}
                    >
                      {initials}
                    </div>
                  </div>

                  {/* Instructor Details */}
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base font-bold text-white tracking-tight leading-tight truncate">
                      {person.name}
                    </h3>
                    <p className="text-xs text-primary font-medium mt-0.5 line-clamp-2">
                      {person.role}
                    </p>
                  </div>
                </div>

                {/* LinkedIn Link */}
                {linkedinHref && (
                  <a
                    href={linkedinHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between gap-1.5 w-full py-2 px-3 rounded-xl border border-white/15 bg-white/[0.04] text-xs font-semibold text-white/90 hover:text-white hover:border-primary hover:bg-primary/10 transition-all duration-200 group"
                    aria-label={`Connect with ${person.name} on LinkedIn`}
                  >
                    <div className="flex items-center gap-1.5">
                      <Linkedin className="w-3.5 h-3.5 text-[#0A66C2] group-hover:scale-110 transition-transform" />
                      <span>LinkedIn Profile</span>
                    </div>
                    <ExternalLink className="w-3 h-3 text-white/50 group-hover:text-primary transition-colors" />
                  </a>
                )}

                {/* Biography */}
                {person.bio && (
                  <p className="text-xs text-white/75 leading-relaxed font-normal pt-3 border-t border-white/5">
                    {person.bio}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
